import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import PositiveListService from "src/modules/data-protection/apis/PositiveListService";
import { getPositiveListErrorKey } from "../helpers/apiError";
import {
    evidenceFromMutation,
    measureFromMutation,
    positiveListDetailKey,
    refreshPositiveListMeasure,
} from "../helpers/cache";

const evidenceList = (measure) => (
    Array.isArray(measure?.evidence) ? measure.evidence : []
);

const evidencePatch = (queryClient, measureId, { measure, added, removedId }) => {
    const current = queryClient.getQueryData(positiveListDetailKey(measureId));
    let evidence = evidenceList(measure);

    if (!Array.isArray(measure?.evidence)) {
        evidence = evidenceList(current);

        if (added) {
            evidence = [
                ...evidence.filter((item) => String(item.id) !== String(added.id)),
                added,
            ];
        }

        if (removedId != null) {
            evidence = evidence.filter((item) => String(item.id) !== String(removedId));
        }
    }

    return {
        evidence,
        evidence_count: evidence.length,
    };
};

const syncEvidence = async (queryClient, softwareId, measureId, change) => {
    const patch = evidencePatch(queryClient, measureId, change);
    const savedIds = new Set(patch.evidence.map((item) => String(item.id)));

    await refreshPositiveListMeasure(queryClient, {
        softwareId,
        measureId,
        patch,
        confirm: (detail) => {
            const ids = new Set(evidenceList(detail).map((item) => String(item.id)));

            if (change.removedId != null && ids.has(String(change.removedId))) {
                return false;
            }

            return [...savedIds].every((id) => ids.has(id));
        },
    });
};

const notifyEvidenceError = (error, t) => {
    if (process.env.NODE_ENV === "development") {
        console.error(error);
    }

    toast(
        t(getPositiveListErrorKey(error) || "An error occurred while saving evidence."),
        { type: "error" }
    );
};

export const useAddPositiveListEvidence = ({ softwareId, t, onSuccess } = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ measureId, payload }) => {
            const service = PositiveListService.getInstance();

            return await service.addEvidence(measureId, payload);
        },
        onSuccess: async (response, variables) => {
            const measure = measureFromMutation(response);

            await syncEvidence(queryClient, softwareId, variables.measureId, {
                measure,
                added: measure ? null : evidenceFromMutation(response),
            });
            toast(t("Evidence added."), { type: "success" });

            if (typeof onSuccess === "function") {
                onSuccess(measure || response);
            }
        },
        onError: (error) => notifyEvidenceError(error, t),
    });
};

export const useRemovePositiveListEvidence = ({ softwareId, measureId, t } = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (evidenceId) => {
            const service = PositiveListService.getInstance();

            return await service.removeEvidence(measureId, evidenceId);
        },
        onSuccess: async (_response, evidenceId) => {
            await syncEvidence(queryClient, softwareId, measureId, {
                removedId: evidenceId,
            });
            toast(t("Evidence removed."), { type: "success" });
        },
        onError: (error) => notifyEvidenceError(error, t),
    });
};
